import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";

export default function MDDateRangePicker({
  value = { startDate: null, endDate: null },
  onChange = () => { },
  placeholder = "Select date range",
  width = "100%",
  required = false,
  disabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(value.startDate ? new Date(value.startDate) : null);
  const [endDate, setEndDate] = useState(value.endDate ? new Date(value.endDate) : null);

  const isMobile = useMediaQuery("(max-width:600px)");
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (!disabled) setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = daysInMonth(year, month);

    const days = [];
    // empty cells before month start
    for (let i = 0; i < firstDay; i++) {
      days.push(<Box key={`empty-${i}`} />);
    }

    for (let i = 1; i <= totalDays; i++) {
      const thisDate = new Date(year, month, i);
      const isSelectedStart =
        startDate && thisDate.toDateString() === startDate.toDateString();
      const isSelectedEnd =
        endDate && thisDate.toDateString() === endDate.toDateString();
      const isInRange =
        startDate && endDate && thisDate > startDate && thisDate < endDate;

      let bg = "transparent";
      let color = "inherit";
      if (isSelectedStart || isSelectedEnd) {
        bg = "#1976d2";
        color = "#fff";
      } else if (isInRange) {
        bg = "#e3f2fd";
      }

      days.push(
        <Box
          key={i}
          onClick={() => handleDateClick(thisDate)}
          sx={{
            height: isMobile ? 28 : 32,
            width: isMobile ? 28 : 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backgroundColor: bg,
            color: color,
            fontSize: isMobile ? "0.6rem" : "0.75rem",
            "&:hover": {
              backgroundColor:
                isSelectedStart || isSelectedEnd ? "#1565c0" : "#e0e0e0",
            },
          }}
        >
          {i}
        </Box>
      );
    }
    return days;
  };

  const handleDateClick = (date) => {
    let newStart = startDate;
    let newEnd = endDate;

    if (!startDate || (startDate && endDate)) {
      newStart = date;
      newEnd = null;
    } else if (date < startDate) {
      newEnd = startDate;
      newStart = date;
    } else {
      newEnd = date;
    }

    setStartDate(newStart);
    setEndDate(newEnd);

    if (newStart && newEnd) {
      onChange({
        startDate: dayjs(newStart).format("YYYY-MM-DD"),
        endDate: dayjs(newEnd).format("YYYY-MM-DD"),
      });
    } else {
      onChange({ startDate: newStart ? dayjs(newStart).format("YYYY-MM-DD") : null, endDate: null });
    }
  };

  const displayValue = () => {
    if (startDate && endDate)
      return `${dayjs(startDate).format("YYYY-MM-DD")} to ${dayjs(endDate).format("YYYY-MM-DD")}`;
    else if (startDate) return dayjs(startDate).format("YYYY-MM-DD");
    else return "";
  };

  return (
    <Box sx={{ width }}>
      <TextField
        fullWidth
        value={displayValue()}
        placeholder={placeholder}
        onClick={handleOpen}
        required={required}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton size="small" onClick={handleOpen} disabled={disabled}>
              <CalendarMonthIcon />
            </IconButton>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: "4px 8px",  // reduced padding
            height: "36px",      // reduce height
          },
          "& input": {
            padding: 0,          // remove inner padding
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(255,255,255,1)",
            boxShadow: 3,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 2, width: isMobile ? 260 : 300 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <IconButton
              size="small"
              fontSize="10px"
              onClick={() =>
                setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
              }
            >
              ◀
            </IconButton>
            <Typography variant="subtitle2" fontWeight="500">
              {currentDate.toLocaleString("default", { month: "long" })} {currentDate.getFullYear()}
            </Typography>
            <IconButton
              size="small"
              fontSize="10px"
              onClick={() =>
                setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
              }
            >
              ▶
            </IconButton>
          </Box>

          {/* Week Days */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" textAlign="center" mb={1}>
            {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
              <Typography key={d} variant="caption" fontWeight="600" fontSize={isMobile ? "0.65rem" : "0.75rem"}>
                {d}
              </Typography>
            ))}
          </Box>

          {/* Days */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.25} justifyItems="center">
            {renderCalendar()}
          </Box>

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <Button
              size="small"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                onChange({ startDate: null, endDate: null });
              }}
            >
              Clear
            </Button>
            <Button size="small" onClick={handleClose} disabled={!startDate}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
